import React from "react";

import { OrderListDto, OrderStatusDto } from "services/orders/restModels";
import { formatDateTime } from "shared/lib/dateTime";
import { orderDeliveryTypeLabel, orderPaymentTypeLabel, orderStatusLabel } from "shared/lib/orderLabels";
import { pluralize } from "shared/lib/plural";
import { DataTable, DataTableColumn, Pagination } from "shared/ui";

import { useOrdersContext } from "features/Orders/context";

import { Muted, OrderNumber, PaymentDot } from "./styles";

// Применим ли к заказу признак оплаты, решает бэк (isOnline): перечислять онлайн-провайдеров
// на клиенте нечего.
const paymentTone = (order: OrderListDto): "paid" | "unpaid" | "refunded" | null => {
    if (order.status === OrderStatusDto.canceledWithRefund) return "refunded";
    if (!order.paymentDetails.isOnline) return null;

    return order.paymentDetails.isPaid ? "paid" : "unpaid";
};

export const OrdersTable = () => {
    const state = useOrdersContext();

    const columns: DataTableColumn<OrderListDto>[] = [
        {
            key: "payment-state",
            role: "indicator",
            render: (order) => {
                const tone = paymentTone(order);

                return tone === null ? null : <PaymentDot $tone={tone} />;
            },
        },
        {
            key: "id",
            title: "Заказ",
            role: "primary",
            // Номер только подкрашен: интерактивна вся строка (onRowClick), а кнопка внутри
            // кликабельной строки — вложенный интерактивный элемент.
            render: (order) => (
                <>
                    <OrderNumber>№{order.id}</OrderNumber>{" "}
                    <Muted>
                        {order.itemsCount}{" "}
                        {pluralize(order.itemsCount, "позиция", "позиции", "позиций")}
                    </Muted>
                </>
            ),
        },
        {
            key: "createdAt",
            title: "Дата",
            role: "secondary",
            width: 150,
            render: (order) => formatDateTime(order.createdAt),
        },
        {
            key: "total",
            title: "Сумма",
            role: "secondary",
            width: 120,
            align: "right",
            render: (order) => state.formatPrice(order.totalPrice),
        },
        {
            key: "delivery",
            title: "Получение",
            role: "secondary",
            width: 170,
            render: (order) => orderDeliveryTypeLabel(order.deliveryType),
        },
        {
            key: "payment",
            title: "Оплата",
            role: "secondary",
            width: 200,
            render: (order) => orderPaymentTypeLabel(order.paymentDetails.type),
        },
        {
            key: "status",
            title: "Статус",
            role: "secondary",
            width: 170,
            render: (order) => orderStatusLabel(order.status),
        },
    ];

    return (
        <DataTable
            columns={columns}
            rows={state.orders}
            rowKey={(order) => order.id}
            onRowClick={(order) => state.openOrder(order.id)}
            footer={
                <Pagination
                    page={state.page}
                    pageSize={state.pageSize}
                    total={state.total}
                    onChange={state.setPage}
                />
            }
        />
    );
};
